"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import type { AuthAdapter, AdapterOptions } from "../adapters/types";
import type { AuthProvider } from "../types";
import { sessionCore } from "../core/session-core";
import { parseJwtUser, parseApiTokens } from "../core/jwt-decode";
import { refreshWithDedup } from "../core/refresh";
import { authApi } from "../api";
import { mergeAdjustAdidIntoHeaders } from "@/src/lib/bridge/adjustRequestHeaders";
import { authActions } from "../store/auth-store";
import {
  TOKEN_REFRESH_INTERVAL_MS,
  TOKEN_REFRESH_THRESHOLD_MS,
} from "../constants";
import {
  coldStartRestore,
  persistLoginType,
  detectLoginType,
} from "./cold-start";
import { startLoginStoreBridge } from "../compat/login-store-bridge";
import { getEnvIndexStorageBaseUrl } from "../../api/osp/constants";
import { getBridge } from "@/src/lib/bridge/bridge";
import { clearBusinessStateOnLogout } from "@store/modules/logoutCleanup";
import { markJustLoggedOut } from "@features/login/authSessionSyncRefs";
import { redirectToLoginPage } from "@features/login/redirectToLoginPage";

export interface LogoutOptions {
  /**
   * 登出后是否跳转登录页，默认 true。
   * 仅 extension 登出弹窗需要 false —— 它清完会话后要 `window.close()`，
   * 跳转会把弹窗带到登录页而不是关掉。
   */
  redirect?: boolean;
}

interface AuthContextValue {
  login: (adapter: AuthAdapter, options?: AdapterOptions) => Promise<void>;
  logout: (options?: LogoutOptions) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * 清除 Firebase Web 端会话。
 *
 * 不清会导致下次三方登录静默复用同一身份，用户无法切换账号。
 * 邮箱 OTP 登录不初始化 Firebase，`getAuth()` 会抛错 —— 必须独立兜住，
 * 否则异常会中断整个登出流程，退化成"点登出没反应"。
 */
async function signOutFirebaseWeb(): Promise<void> {
  try {
    const { getAuth, signOut } = await import("firebase/auth");
    await signOut(getAuth());
  } catch {
    // Firebase 未初始化（邮箱登录）或已登出：忽略
  }
}

export function AuthOrchestrator({ children }: { children: ReactNode }) {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    coldStartRestore();
    checkRedirectResult();
    startRefreshTimer();
    const stopLoginStoreBridge = startLoginStoreBridge();

    const stopStorageSync = setupStorageSync();
    const stopBridgeSync = setupBridgeSync();

    return () => {
      stopRefreshTimer();
      stopLoginStoreBridge();
      stopStorageSync();
      stopBridgeSync();
    };
  }, []);

  async function checkRedirectResult() {
    try {
      const { getAuth, getRedirectResult } = await import("firebase/auth");
      const auth = getAuth();
      const result = await getRedirectResult(auth);
      if (!result) {
        return;
      }
      const idToken = await result.user.getIdToken();
      const provider =
        (result.providerId?.replace(".com", "") as AuthProvider) ?? "google";
      const apiResult = await authApi.login(
        mergeAdjustAdidIntoHeaders({ "OS-Firebase-Token": idToken }),
      );
      const tokens = parseApiTokens(apiResult);
      sessionCore.setTokens(tokens);
      const user = parseJwtUser(tokens.accessToken, provider);
      persistLoginType(provider);
      authActions.setAuthenticated(user, tokens);
    } catch {
      // no pending redirect or redirect failed: ignore
    }
  }

  function startRefreshTimer() {
    timerRef.current = setInterval(() => {
      if (
        sessionCore.isExpiringSoon(TOKEN_REFRESH_THRESHOLD_MS) &&
        sessionCore.canRefresh()
      ) {
        refreshWithDedup().then((refreshed) => {
          if (refreshed) {
            authActions.updateTokens(refreshed);
          } else {
            sessionCore.clear();
            authActions.reset();
          }
        });
      }
    }, TOKEN_REFRESH_INTERVAL_MS);
  }

  function stopRefreshTimer() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function setupStorageSync(): () => void {
    if (typeof window === "undefined") {
      return () => {};
    }

    const credentialsKey = `osp.${getEnvIndexStorageBaseUrl(process.env.NEXT_PUBLIC_ENV ?? "dev")}.credentials`;
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== credentialsKey) {
        return;
      }
      if (event.newValue) {
        const tokens = sessionCore.getTokens();
        if (tokens) {
          try {
            const user = parseJwtUser(tokens.accessToken, detectLoginType());
            authActions.setAuthenticated(user, tokens);
          } catch {
            /* parse failed, ignore */
          }
        }
      } else {
        authActions.reset();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }

  function setupBridgeSync(): () => void {
    if (typeof window === "undefined") {
      return () => {};
    }

    const handleBridgeMessage = async (event: MessageEvent) => {
      const data = event.data;

      if (data?.type === "auth-session-updated") {
        const tokens = sessionCore.getTokens();
        if (tokens) {
          try {
            const user = parseJwtUser(tokens.accessToken, detectLoginType());
            authActions.setAuthenticated(user, tokens);
          } catch {
            /* parse failed, ignore */
          }
        }
      }
    };
    window.addEventListener("message", handleBridgeMessage);
    return () => window.removeEventListener("message", handleBridgeMessage);
  }

  const login = async (adapter: AuthAdapter, options?: AdapterOptions) => {
    authActions.startAuth(adapter.provider);
    try {
      const adapterResult = await adapter.authenticate(options);
      const apiResult = await authApi.login(
        mergeAdjustAdidIntoHeaders(adapterResult.authHeaders),
      );
      const tokens = parseApiTokens(apiResult);
      sessionCore.setTokens(tokens);
      const user = parseJwtUser(tokens.accessToken, adapter.provider);
      persistLoginType(adapter.provider);
      authActions.setAuthenticated(user, tokens);
      try {
        const bridge = getBridge();
        if (bridge.isInApp()) {
          bridge.broadcastAuthSessionUpdated({
            userId: user.uid,
          });
        }
      } catch {
        // non-app: ignore
      }
    } catch (error) {
      authActions.setError({
        type: "provider_error",
        provider: adapter.provider,
        code: (error as Error).name ?? "UNKNOWN",
        message: (error as Error).message ?? "Login failed",
      });
      throw error;
    } finally {
      adapter.destroy?.();
    }
  };

  /**
   * 统一登出入口 —— 所有登出 UI 必须走这里。
   *
   * 顺序有约束：token / 业务缓存必须全部清完再通知原生 reload 各 Tab，
   * 否则被 reload 的 WebView 会读到尚未清除的旧登录态。
   */
  const logout = async (options?: LogoutOptions) => {
    stopRefreshTimer();
    sessionCore.clear();
    authActions.reset();
    clearBusinessStateOnLogout();
    await signOutFirebaseWeb();
    // 须在 localStorage / 业务缓存全部清完后写标记，熔断"刚登出又被缓存恢复"
    markJustLoggedOut();
    try {
      const bridge = getBridge();
      if (bridge.isInApp()) {
        bridge.nativeFirebaseLogout();
        bridge.closeDetailInNative();
        bridge.syncShellTabsAfterLogout();
      }
    } catch {
      // non-app or old shell: ignore
    }
    if (options?.redirect !== false) {
      redirectToLoginPage();
    }
  };

  return (
    <AuthContext.Provider value={{ login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within AuthOrchestrator");
  }
  return ctx;
}
